import { useEffect, useRef } from "react";
import { pricing } from "@/mocks/pricing";

const TELEGRAM_URL = "https://t.me/Vertttess";

export default function PricingSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el, i) => {
      (el as HTMLElement).style.animationDelay = `${i * 0.12}s`;
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="pricing"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-background-100"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-14 md:mb-20">
          <span className="reveal opacity-0 inline-block px-4 py-1.5 text-xs font-medium tracking-widest uppercase bg-primary-100 text-primary-700 rounded-full mb-4">
            Прозрачные цены
          </span>
          <h2 className="reveal opacity-0 font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-900 mb-4">
            Стоимость услуг
          </h2>
          <p className="reveal opacity-0 text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
            Три тарифа под разные потребности — от базового подбора до
            полной цифровизации гостиничного бизнеса
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 items-stretch">
          {pricing.map((plan, index) => (
            <div
              key={plan.id}
              className={`reveal opacity-0 relative flex flex-col p-6 md:p-8 rounded-lg border transition-all duration-300 ${
                plan.popular
                  ? "bg-primary-500 text-white border-primary-500 md:-my-4 md:py-12 shadow-xl"
                  : "bg-background-50 border-background-200/70 hover:border-primary-300/60"
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 text-xs font-semibold bg-accent-500 text-white rounded-full whitespace-nowrap">
                  Самый популярный
                </span>
              )}

              {/* Plan Name */}
              <h3
                className={`font-heading text-2xl md:text-3xl mb-2 ${
                  plan.popular ? "text-white" : "text-foreground-900"
                }`}
              >
                {plan.name}
              </h3>

              {/* Price */}
              <div className="mb-1">
                <span
                  className={`font-heading text-3xl md:text-4xl font-bold ${
                    plan.popular ? "text-white" : "text-primary-600"
                  }`}
                >
                  {plan.price}
                </span>
              </div>
              <span
                className={`text-sm mb-5 ${
                  plan.popular ? "text-white/80" : "text-foreground-500"
                }`}
              >
                {plan.period}
              </span>

              {/* Description */}
              <p
                className={`text-sm mb-6 ${
                  plan.popular ? "text-white/90" : "text-foreground-600"
                }`}
              >
                {plan.description}
              </p>

              {/* Features */}
              <ul className="flex-1 space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span
                      className={`mt-0.5 shrink-0 w-5 h-5 flex items-center justify-center rounded-full ${
                        plan.popular
                          ? "bg-white/20 text-white"
                          : "bg-primary-100 text-primary-600"
                      }`}
                    >
                      <i className="ri-check-line text-xs" />
                    </span>
                    <span
                      className={`text-sm ${
                        plan.popular ? "text-white/90" : "text-foreground-700"
                      }`}
                    >
                      {feature}
                    </span>
                  </li>
                ))}
                {plan.notIncluded.map((item, i) => (
                  <li key={`not-${i}`} className="flex items-start gap-2.5 opacity-50">
                    <span
                      className={`mt-0.5 shrink-0 w-5 h-5 flex items-center justify-center rounded-full ${
                        plan.popular
                          ? "bg-white/10 text-white/50"
                          : "bg-background-200 text-foreground-400"
                      }`}
                    >
                      <i className="ri-close-line text-xs" />
                    </span>
                    <span
                      className={`text-sm line-through ${
                        plan.popular ? "text-white/60" : "text-foreground-400"
                      }`}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              {plan.price === "Обсудить" ? (
                <a
                  href={TELEGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    plan.popular
                      ? "bg-white text-primary-600 hover:bg-background-100"
                      : "bg-primary-500 text-white hover:bg-primary-600"
                  }`}
                >
                  Обсудить с нами
                  <i className="ri-arrow-right-line" />
                </a>
              ) : (
                <a
                  href={TELEGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    plan.popular
                      ? "bg-white text-primary-600 hover:bg-background-100"
                      : "bg-primary-500 text-white hover:bg-primary-600"
                  }`}
                >
                  Выбрать тариф
                  <i className="ri-arrow-right-line" />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}